import type { ReactElement } from "react";
import {
  IconClipboardData,
  IconInfoCircle,
  IconLeaf,
  IconRulerMeasure,
  IconShovel,
  IconSunset2
} from "@tabler/icons-react";
import type { EnvironmentalResponse, PropertySummary } from "@shared/api";
import type { BuildabilityScore } from "../../score/computeBuildabilityScore";
import type { SiteWorkEstimate } from "../../score/computeSiteWorkEstimate";
import type {
  ExternalContextReportData,
  LotAnalysisReportData,
  ParcelIntelReportData,
  SunViewsReportData
} from "../createPropertyReport";
import { EnvironmentalRiskCard } from "../inspector/EnvironmentalRiskCard";
import { GeneralLayersCard } from "../inspector/GeneralLayersCard";
import { MeasurementsLayersCard } from "../inspector/MeasurementsLayersCard";
import {
  PropertyReportMetricsCard,
  type PropertySummaryStatus
} from "../inspector/PropertyReportMetricsCard";
import { SiteWorkCard } from "../inspector/SiteWorkCard";
import { SunViewsMetricsCard } from "../inspector/SunViewsMetricsCard";
import {
  IconRail,
  RAIL_ICON_SIZE,
  RAIL_ICON_STROKE,
  type RailModule
} from "../rail/IconRail";
import styles from "./ReportRail.module.css";

/**
 * Report section ids. `floodRisk` is the historical id for the
 * Environmental Risk section (kept so persisted / linked state keeps
 * resolving); the visible label is "Environment".
 */
export type ReportRailModuleId =
  | "propertyReport"
  | "general"
  | "measurements"
  | "sunViews"
  | "floodRisk"
  | "siteWork";

/** Ordered section specs (icon + label). Shared with the handset strip. */
export const REPORT_RAIL_MODULES: ReadonlyArray<{
  id: ReportRailModuleId;
  label: string;
  Icon: typeof IconClipboardData;
}> = [
  { id: "propertyReport", label: "Buildability score", Icon: IconClipboardData },
  { id: "general", label: "Parcel", Icon: IconInfoCircle },
  { id: "measurements", label: "Measurements", Icon: IconRulerMeasure },
  { id: "sunViews", label: "Sun & views", Icon: IconSunset2 },
  { id: "floodRisk", label: "Environment", Icon: IconLeaf },
  { id: "siteWork", label: "Site work", Icon: IconShovel }
];

export function reportModuleIcon(id: ReportRailModuleId): ReactElement {
  const spec = REPORT_RAIL_MODULES.find((m) => m.id === id)!;
  return <spec.Icon size={RAIL_ICON_SIZE} stroke={RAIL_ICON_STROKE} />;
}

/** Everything the report sections read. Getters are called on every render. */
export interface ReportRailData {
  buildabilityScore: BuildabilityScore | null;
  summary: PropertySummary | null;
  summaryStatus: PropertySummaryStatus;
  parcelIntel: ParcelIntelReportData | null;
  siteCenter: { lat: number; lon: number };
  lot: LotAnalysisReportData | null;
  /** Interactive tape measure (desktop only; omit on handheld). */
  measureActive?: boolean;
  onMeasureToggle?: () => void;
  sunViewsReport: SunViewsReportData;
  externalReport: ExternalContextReportData | null;
  environmental: EnvironmentalResponse | null;
  siteWorkEstimate: SiteWorkEstimate;
  onPrintHandoff?: () => void;
}

export interface ReportRailProps {
  data: ReportRailData;
  activeModule: ReportRailModuleId | null;
  onSelectModule: (id: ReportRailModuleId | null) => void;
}

/**
 * Right-edge property report: the left rail's twin. The top button carries
 * the live buildability score instead of a glyph; every other section is a
 * read-only metric body.
 */
export function ReportRail({
  data,
  activeModule,
  onSelectModule
}: ReportRailProps): ReactElement {
  const modules: RailModule<ReportRailModuleId>[] = REPORT_RAIL_MODULES.map(
    (m) => ({
      id: m.id,
      label: m.label,
      icon:
        m.id === "propertyReport" ? (
          <ScoreGlyph score={data.buildabilityScore} />
        ) : (
          <m.Icon size={RAIL_ICON_SIZE} stroke={RAIL_ICON_STROKE} />
        ),
      render: () => renderReportModuleBody(m.id, data, "inspector")
    })
  );

  return (
    <IconRail
      side="right"
      ariaLabel="Property report"
      modules={modules}
      activeId={activeModule}
      onSelect={onSelectModule}
    />
  );
}

/** Live score numeral in the rail button; falls back to the clipboard glyph. */
export function ScoreGlyph({
  score
}: {
  score: BuildabilityScore | null;
}): ReactElement {
  if (score == null) {
    return <IconClipboardData size={RAIL_ICON_SIZE} stroke={RAIL_ICON_STROKE} />;
  }
  return (
    <span
      className={styles.scoreGlyph}
      title={`Buildability ${score.total}/100 (${score.band})`}
    >
      {score.total}
    </span>
  );
}

/**
 * Body for one report section. Shared by the desktop flyout and the handset
 * bottom sheet (`presentation="drawer"`).
 */
export function renderReportModuleBody(
  id: ReportRailModuleId,
  data: ReportRailData,
  presentation: "inspector" | "drawer"
): ReactElement | null {
  switch (id) {
    case "propertyReport":
      return (
        <PropertyReportMetricsCard
          buildabilityScore={data.buildabilityScore}
          summary={data.summary}
          summaryStatus={data.summaryStatus}
          presentation={presentation}
        />
      );
    case "general":
      return (
        <GeneralLayersCard
          parcelIntel={data.parcelIntel}
          siteCenter={data.siteCenter}
          presentation={presentation}
        />
      );
    case "measurements":
      return (
        <MeasurementsLayersCard
          lot={data.lot}
          measureActive={data.measureActive}
          onMeasureToggle={
            presentation === "inspector" ? data.onMeasureToggle : undefined
          }
          presentation={presentation}
        />
      );
    case "sunViews":
      return (
        <SunViewsMetricsCard report={data.sunViewsReport} presentation={presentation} />
      );
    case "floodRisk":
      return (
        <EnvironmentalRiskCard
          external={data.externalReport}
          environmental={data.environmental}
          presentation={presentation}
        />
      );
    case "siteWork":
      return (
        <SiteWorkCard
          estimate={data.siteWorkEstimate}
          onPrintHandoff={data.onPrintHandoff}
          presentation={presentation}
        />
      );
    default:
      return null;
  }
}
